import { useEffect, useRef, useState } from "react";
import { withStyles } from "@material-ui/core/styles";
import ToggleButton from "@material-ui/lab/ToggleButton";
import ToggleButtonGroup from "@material-ui/lab/ToggleButtonGroup";
import moment from "moment";
import { Modal, Button, Form } from "react-bootstrap";
import { MultiSelect } from "react-multi-select-component";
import { Chip } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLocationDot, faTimes } from "@fortawesome/free-solid-svg-icons";
import TextField from "../GenericComponents/TextField";
import SelectField from "../GenericComponents/SelectField";
import DatePickerField from "../GenericComponents/DatePickerField";
import CustomRadioButton from "../GenericComponents/CustomRadioButton";
import GeofenceCustomArea, {
  GeofenceAddressSearch,
} from "../GenericComponents/MapComponents/GeofenceCustomArea";
import "./GeofenceSettingsModal.css";

export const MAPS_MODE = {
  CIRCLE: "circle",
  RECTANGLE: "rectangle",
};

const MONITORING_MODE = {
  ALWAYS: "ALWAYS",
  SCHEDULED: "SCHEDULED",
};

const DAYS = [
  {
    key: "monday",
    label: "M",
  },
  {
    key: "tuesday",
    label: "T",
  },
  {
    key: "wednesday",
    label: "W",
  },
  {
    key: "thursday",
    label: "T",
  },
  {
    key: "friday",
    label: "F",
  },
  {
    key: "saturday",
    label: "S",
  },
  {
    key: "sunday",
    label: "S",
  },
];

const StyledToggleButtonGroup = withStyles(() => ({
  grouped: {
    margin: 0,
    padding: 0,
    border: "none",
    "&:not(:first-child)": {
      border: "none",
      borderRadius: "999px",
      marginLeft: 0,
    },
    "&:first-child": {
      border: "none",
      borderRadius: "999px",
    },
  },
}))(ToggleButtonGroup);

const StyledToggle = withStyles({
  root: {
    color: "#0369a1",
    background: "#f0f9ff",
    border: "1.5px solid #bae6fd",
    borderRadius: "999px",
    "&$selected": {
      color: "#fff",
      background: "#0077b6",
      borderColor: "#0077b6",
    },
    "&:hover": {
      borderColor: "#7dd3fc",
      background: "#e0f2fe",
    },
    "&:hover$selected": {
      borderColor: "#0077b6",
      background: "#0369a1",
    },
    minWidth: 36,
    maxWidth: 36,
    height: 36,
    textTransform: "unset",
    fontSize: "0.75rem",
    fontWeight: 700,
    margin: 0,
  },
  selected: {},
})(ToggleButton);

const GeofenceSettingsModal = ({
  open,
  circleData,
  boundsData,
  fenceType,
  handleClose,
  saveGeoLocationData,
  updateGeoLocationData,
  updateFlag,
  updatedData,
  geoLocationAlertTypeOptions,
  patientContacts,
  deviceType,
}) => {
  const [circle, setCircle] = useState(circleData || {});
  const [bounds, setBounds] = useState(boundsData || {});
  const [defaultZoom, setDefaultZoom] = useState(15);
  const [locationName, setLocationName] = useState();
  const [alertType, setAlertType] = useState();
  const [alwaysOn, setAlwaysOn] = useState(false);
  const [alertStartTime, setAlertStartTime] = useState(moment().toDate());
  const [alertEndTime, setAlertEndTime] = useState(moment().toDate());
  const [days, setDays] = useState([]);
  const [alertContacts, setAlertContacts] = useState([]);
  const [otherAlertContacts, setOtherAlertContacts] = useState([]);
  const [mapHeight, setMapHeight] = useState("500px");
  const [mapInstance, setMapInstance] = useState(null);

  const lastBoundRef = useRef(bounds);
  const [clear, setClear] = useState(0);

  const handleChange = (e) => {
    let fieldValue = e.target.value;
    setLocationName(fieldValue);
  };

  const handleSelectChange = (e) => {
    let fieldValue = e.target.value;
    setAlertType(fieldValue);
  };

  const handleAlwaysOnChange = (_fieldName, e) => {
    setAlwaysOn(e.target.checked);
  };

  const handleAlertStartTime = (fieldName, date) => {
    let fieldValue = date;
    setAlertStartTime(fieldValue);
  };

  const handleAlertEndTime = (fieldName, date) => {
    let fieldValue = date;
    setAlertEndTime(fieldValue);
  };

  const handleClear = () => {
    setCircle({});
    setBounds({});
    setClear(clear + 1);
  };

  const handleCancel = () => {
    handleClose();
  };

  const buildSchedulePayload = () => {
    if (alwaysOn) {
      return {
        monitoringMode: MONITORING_MODE.ALWAYS,
      };
    }

    return {
      monitoringMode: MONITORING_MODE.SCHEDULED,
      fenceAlertDays: days,
      fenceAlertTiming: days.map((day) => ({
        weekday: day,
        startTime: moment(alertStartTime).format("HH:mm"),
        endTime: moment(alertEndTime).format("HH:mm"),
      })),
    };
  };

  const buildAlertContactsPayload = () => {
    const enabledAlertContacts = alertContacts.map((val) => val.value);
    const newAlertContacts = otherAlertContacts.map((val) => val.value);
    return { enabledAlertContacts, newAlertContacts };
  };

  const buildLocationPayload = () => {
    const schedule = buildSchedulePayload();
    const { enabledAlertContacts } = buildAlertContactsPayload();
    return {
      locationName: locationName,
      fenceType: circle.radius !== 0 ? "CIRCLE" : "RECTANGLE",
      fenceAlertType: alertType,
      monitoringMode: schedule.monitoringMode,
      ...(schedule.monitoringMode === MONITORING_MODE.SCHEDULED
        ? {
            fenceAlertDays: schedule.fenceAlertDays,
            fenceAlertTiming: schedule.fenceAlertTiming,
          }
        : {}),
      geoFenceData:
        circle.radius !== 0
          ? {
              center: {
                lat: circle.center.lat,
                lng: circle.center.lng,
              },
              radius: circle.radius,
            }
          : {
              east: bounds.east,
              west: bounds.west,
              north: bounds.north,
              south: bounds.south,
            },
      alertContacts: enabledAlertContacts,
    };
  };

  const handleSave = () => {
    saveGeoLocationData(buildLocationPayload());
  };

  const handleUpdate = () => {
    updateGeoLocationData({
      ...buildLocationPayload(),
      locationId: updatedData.id,
    });
  };

  useEffect(() => {
    const updateMapHeight = () => {
      if (window.innerWidth < 768) {
        setMapHeight("280px");
      } else if (window.innerWidth < 992) {
        setMapHeight("420px");
      } else {
        setMapHeight("500px");
      }
    };

    updateMapHeight();
    window.addEventListener("resize", updateMapHeight);
    return () => window.removeEventListener("resize", updateMapHeight);
  }, []);

  useEffect(() => {
    if (circleData) {
      setCircle(circleData);
    } else {
      setDefaultZoom(3);
      setCircle({});
    }
  }, [circleData]);

  useEffect(() => {
    if (boundsData) {
      setBounds(boundsData);
      lastBoundRef.current = boundsData;
    } else {
      setDefaultZoom(3);
      setBounds({});
    }
  }, [boundsData]);

  useEffect(() => {
    lastBoundRef.current = bounds;
  }, [bounds]);

  useEffect(() => {
    if (Object.keys(updatedData).length > 0) {
      let contacts = [];
      if (updatedData.alertContacts.length !== 0) {
        updatedData.alertContacts.map((val) => {
          let contact = {
            label: `${val.firstName} ${val.lastName}`,
            value: val.id,
            phoneNumber: val.phoneNumber,
          };
          contacts.push(contact);
        });
      }
      const isAlwaysOn =
        updatedData.monitoringMode === MONITORING_MODE.ALWAYS;
      setLocationName(updatedData.locationName);
      setAlertType(updatedData.fenceAlertType);
      setAlwaysOn(isAlwaysOn);
      if (isAlwaysOn) {
        setDays([]);
        setAlertStartTime(moment().toDate());
        setAlertEndTime(moment().toDate());
      } else {
        setDays(updatedData.fenceAlertDays || []);
        setAlertStartTime(
          moment(
            updatedData.fenceAlertTiming?.[0]?.startTime || "00:00",
            "HH:mm",
          ).toDate(),
        );
        setAlertEndTime(
          moment(
            updatedData.fenceAlertTiming?.[0]?.endTime || "23:55",
            "HH:mm",
          ).toDate(),
        );
      }
      setAlertContacts(contacts);
    } else {
      setLocationName("");
      setAlertType("");
      setAlwaysOn(false);
      setDays([]);
      setAlertStartTime(moment().toDate());
      setAlertEndTime(moment().toDate());
      setAlertContacts([]);
    }
  }, [updatedData]);

  const valueRenderer = () => {
    return "Search or select contacts to alert";
  };

  const handleDelete = (cont) => {
    let tempAlertContatcs = alertContacts
      .filter((val) => {
        return val.value !== cont.value;
      })
      .map((contact) => {
        return contact;
      });
    setAlertContacts(tempAlertContatcs);
  };

  return (
    <Modal
      size="xl"
      show={open}
      onHide={handleClose}
      centered={true}
      backdrop="static"
      keyboard={false}
      dialogClassName="geofence-settings-modal"
    >
      <div className="geofence-modal-header">
        <div className="geofence-modal-header-left">
          <div className="geofence-modal-header-icon">
            <FontAwesomeIcon icon={faLocationDot} color="#e03e3e" />
          </div>
          <div>
            <h2 className="geofence-modal-title">
              {!updateFlag ? "Set Safe Location" : "Update Safe Location"}
            </h2>
            <p className="geofence-modal-subtitle">
              Define the area, alert schedule, and contacts for this safe
              location
            </p>
          </div>
        </div>
        <button
          type="button"
          className="geofence-modal-close"
          onClick={handleClose}
          aria-label="Close"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>

      <Modal.Body className="geofence-modal-body">
        <section className="geofence-modal-section">
          <h3 className="geofence-modal-section-title">Location details</h3>
          <div className="geofence-field-grid">
            <div className="geofence-field">
              <Form.Label>Safe Location Name</Form.Label>
              <TextField
                type={"text"}
                placeholder={"E.g., Home, School, Office"}
                name={"name"}
                value={locationName}
                action={handleChange}
                isAlphaNumeric={true}
                required={true}
              />
            </div>
            <div className="geofence-field">
              <Form.Label>Search by address</Form.Label>
              <GeofenceAddressSearch map={mapInstance} />
            </div>
          </div>
        </section>

        <section className="geofence-modal-section geofence-modal-section-map">
          <h3 className="geofence-modal-section-title">Map & boundary</h3>
          <p className="geofence-helper-text">
            Define how far from the pinned location counts as &quot;inside&quot;
            or &quot;outside&quot; this location
          </p>
          <div className="geofence-map-shell">
            <GeofenceCustomArea
              zoom={defaultZoom}
              height={mapHeight}
              hideSearchBar
              onMapReady={setMapInstance}
              setBounds={(payload) => {
                setBounds(payload);
                lastBoundRef.current = payload;
              }}
              bounds={bounds}
              center={circleData?.center}
              radius={circleData?.radius}
              setCircle={(payload) => {
                setCircle(payload);
              }}
              clearClick={clear}
              setClear={setClear}
              fenceType={fenceType}
              circleValues={circle}
              deviceType={deviceType}
            />
          </div>
        </section>

        <section className="geofence-modal-section">
          <h3 className="geofence-modal-section-title">Alert settings</h3>
          <div
            className="geofence-field"
            style={{ maxWidth: 420, marginBottom: "1rem" }}
          >
            <Form.Label>Send Alert</Form.Label>
            <SelectField
              name={"alertType"}
              options={geoLocationAlertTypeOptions}
              value={alertType}
              action={handleSelectChange}
            />
          </div>

          <div className="geofence-always-on-row">
            <div className="geofence-always-on-copy">
              <div className="geofence-always-on-label">Always On</div>
              <p className="geofence-helper-text">
                Continuously monitors your safe zone and sends trigger alerts.
                Turning this on may use more battery.
              </p>
            </div>
            <CustomRadioButton
              name={"alwaysOn"}
              type={"switch"}
              inline={true}
              label={""}
              checked={alwaysOn}
              action={handleAlwaysOnChange}
            />
          </div>

          {!alwaysOn && (
            <div className="geofence-alert-grid">
              <div>
                <Form.Label>Days</Form.Label>
                <p className="geofence-helper-text">
                  Alerts will only be sent on the selected days.
                </p>
                <div className="geofence-days-toggle">
                  <StyledToggleButtonGroup
                    size="small"
                    arial-label="Days of the week"
                    value={days}
                    onChange={(event, value) => setDays(value)}
                  >
                    {DAYS.map((day, index) => (
                      <StyledToggle
                        key={day.key}
                        value={index + 1}
                        aria-label={day.key}
                      >
                        {day.label}
                      </StyledToggle>
                    ))}
                  </StyledToggleButtonGroup>
                </div>
              </div>

              <div>
                <Form.Label>Duration</Form.Label>
                <p className="geofence-helper-text">
                  Alerts will be sent between the selected start and end times.
                </p>
                <div className="geofence-duration-panel">
                  <div className="geofence-time-field">
                    <div className="geofence-time-label">Start Time</div>
                    <DatePickerField
                      name={"alertStartTime"}
                      minTime={moment("00:00:00", "hh:mm:ss").toDate()}
                      maxTime={moment("23:55:00", "hh:mm:ss").toDate()}
                      value={alertStartTime}
                      action={handleAlertStartTime}
                      showTimeSelect={true}
                      showTimeSelectOnly={true}
                      timeIntervals={5}
                      timeCaption="Time"
                      dateFormat="h:mm aa"
                      className={"form-control"}
                    />
                  </div>
                  <div className="geofence-time-field">
                    <div className="geofence-time-label">End Time</div>
                    <DatePickerField
                      name={"alertEndTime"}
                      minTime={moment("00:00:00", "hh:mm:ss").toDate()}
                      maxTime={moment("23:55:00", "hh:mm:ss").toDate()}
                      value={alertEndTime}
                      action={handleAlertEndTime}
                      showTimeSelect={true}
                      showTimeSelectOnly={true}
                      timeIntervals={5}
                      timeCaption="Time"
                      dateFormat="h:mm aa"
                      className={"form-control"}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>

        <section className="geofence-modal-section">
          <h3 className="geofence-modal-section-title">Alert contacts</h3>
          <Form.Label>Who should be notified?</Form.Label>
          <MultiSelect
            className="geofence-contacts-select"
            options={patientContacts.alertContactList}
            value={alertContacts}
            onChange={setAlertContacts}
            valueRenderer={valueRenderer}
            labelledBy={"Search or select contacts to alert"}
            overrideStrings={{
              selectSomeItems: "Search or select contacts to alert",
            }}
            ClearSelectedIcon={null}
          />
          {alertContacts.length > 0 && (
            <div className="geofence-contact-chips">
              {alertContacts.map((cont, index) => (
                <span
                  key={cont.value || index}
                  className="geofence-contact-chip"
                >
                  <Chip
                    label={`${cont.label} · ${cont.phoneNumber}`}
                    onDelete={() => handleDelete(cont)}
                    size="small"
                  />
                </span>
              ))}
            </div>
          )}
        </section>
      </Modal.Body>

      <Modal.Footer className="geofence-modal-footer">
        <Button onClick={handleCancel} variant="outline-primary">
          Cancel
        </Button>
        <Button onClick={handleClear} variant="outline-primary">
          Clear
        </Button>
        <Button
          onClick={!updateFlag ? handleSave : handleUpdate}
          autoFocus
          variant="primary"
          disabled={
            !locationName ||
            !alertType ||
            (!alwaysOn && days.length === 0) ||
            (circle.radius === 0 && Object.keys(bounds).length === 0)
          }
        >
          {!updateFlag ? "Save Location" : "Update Location"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default GeofenceSettingsModal;
